import { Menu, X } from "lucide-react";
import { PropsWithChildren, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  company,
  footerHighlights,
  footerProcess,
  navItems
} from "../content/site";
import { Eyebrow } from "./SectionHeading";

export function SiteShell({ children }: PropsWithChildren) {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const revealNodes = Array.from(document.querySelectorAll<HTMLElement>("[data-reveal]"));

    if (!("IntersectionObserver" in window)) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.12 }
    );

    revealNodes.forEach((node) => {
      const rect = node.getBoundingClientRect();

      if (rect.top < window.innerHeight * 0.92) {
        node.classList.add("is-visible");
        return;
      }

      observer.observe(node);
    });

    return () => observer.disconnect();
  }, [pathname]);

  return (
    <>
      <header className="site-header">
        <div className="utility-bar">
          <span>{company.name}</span>
          <span>{company.descriptor}</span>
        </div>
        <nav className="main-nav" aria-label="Main navigation">
          <NavLink className="brand" to="/" onClick={() => setIsOpen(false)}>
            <img src="/border.svg" alt={company.name} />
          </NavLink>
          <button
            className="icon-button nav-toggle"
            type="button"
            aria-label={isOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isOpen ? "true" : "false"}
            onClick={() => setIsOpen((value) => !value)}
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className={`nav-links ${isOpen ? "is-open" : ""}`}>
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => (isActive ? "active" : undefined)}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
      <main>{children}</main>
      <footer className="site-footer" data-reveal>
        <div className="footer-panel">
          <div className="footer-lead">
            <Eyebrow>Ready for Review</Eyebrow>
            <strong>{company.name}</strong>
            <p>
              A fabrication partner for project teams that need practical review,
              clean production handoffs, and durable metalwork.
            </p>
            <div className="footer-license" aria-label="License and insurance information">
              <span>{company.licenseHeadline}</span>
              <p>{company.licenseNumbers}</p>
            </div>
            <NavLink className="button primary" to="/contact">
              Start a Project
            </NavLink>
          </div>
          <div className="footer-column">
            <span>Contact</span>
            <a href={`mailto:${company.email}`}>{company.email}</a>
            <a href={company.phoneHref}>{company.phone}</a>
            <p>{company.address}</p>
            <p>{company.hours}</p>
          </div>
          <div className="footer-column">
            <span>Pages</span>
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href}>
                {item.label}
              </NavLink>
            ))}
          </div>
          <div className="footer-column">
            <span>Capabilities</span>
            {footerHighlights.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </div>
          <div className="footer-process">
            <span>Project Path</span>
            <ol>
              {footerProcess.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </div>
      </footer>
    </>
  );
}
